import { useCallback, useState } from 'react';
import { useApiServices } from '@shared/lib/use-api-services';
import type { CreateInvoicePayload } from '@shared/api/contracts';

export interface CreateInvoiceFormValues {
  projectId: string;
  amount: string;
  currencyCode: string;
  expires: number | '';
}

export const useCreateInvoice = () => {
  const services = useApiServices();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const createInvoice = async (values: CreateInvoiceFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      const payload: CreateInvoicePayload = {
        projectId: values.projectId,
        amount: values.amount,
        currencyCode: values.currencyCode,
        expires: Number(values.expires),
      };

      await services.invoices.createInvoice(payload);
      return { success: true as const };
    } catch (err: unknown) {
      const message =
        (err as { message?: string; _data?: { message?: string } })?.message ||
        (err as { _data?: { message?: string } })?._data?.message ||
        'Failed to create invoice';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createInvoice,
    isLoading,
    error,
    clearError,
  };
};
